
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AppSettings {
  defaultPricingTableId?: string;
  defaultDocumentGroups?: number[];
  accountHolderName?: string;
  bankName?: string;
  accountNumber?: string;
  routingNumber?: string;
  accountType?: string;
  wireInstructions?: string;
  financeNotes?: string;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
}

// Define a type for the platform settings explicitly
interface PlatformSettings {
  id: number;
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  routing_number: string;
  account_type: string;
  wire_instructions: string;
  finance_notes: string;
  billing_address: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
  created_at: string;
  updated_at: string;
}

export const useAppSettings = () => {
  const [settings, setSettings] = useState<AppSettings>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from both tables: management_companies for defaults and platform_settings for financial info
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        
        // First get default pricing table and document groups from management_companies
        const { data: mgmtCompanyData, error: mgmtError } = await supabase
          .from('management_companies')
          .select('default_pricing_table_id, default_document_groups')
          .eq('id', 1)  // Assuming record with ID 1 contains platform defaults
          .single();
        
        if (mgmtError) console.error("Error loading management company defaults:", mgmtError);

        // For platform_settings, use a direct table query with type assertion
        const { data: financialData, error: financeError } = await supabase
          .from('platform_settings')
          .select('*')
          .eq('id', 1)
          .single();
        
        if (financeError) {
          console.error("Error loading financial settings:", financeError);
          // Fallback to empty values if query fails
          setSettings({
            defaultPricingTableId: mgmtCompanyData?.default_pricing_table_id?.toString(),
            defaultDocumentGroups: mgmtCompanyData?.default_document_groups || [],
            accountHolderName: '',
            bankName: '',
            accountNumber: '',
            routingNumber: '',
            accountType: '',
            wireInstructions: '',
            financeNotes: '',
            billingAddress: '',
            billingCity: '',
            billingState: '',
            billingZip: ''
          });
          return;
        }
        
        // Convert data to our typed interface using "as unknown as" to bypass type checking
        const typedFinancialData = financialData as unknown as PlatformSettings;
        
        // Combine the results
        setSettings({
          // Default settings from management_companies
          defaultPricingTableId: mgmtCompanyData?.default_pricing_table_id?.toString(),
          defaultDocumentGroups: mgmtCompanyData?.default_document_groups || [],

          // Financial settings from platform_settings
          accountHolderName: typedFinancialData?.account_holder_name || '',
          bankName: typedFinancialData?.bank_name || '',
          accountNumber: typedFinancialData?.account_number || '',
          routingNumber: typedFinancialData?.routing_number || '',
          accountType: typedFinancialData?.account_type || '',
          wireInstructions: typedFinancialData?.wire_instructions || '',
          financeNotes: typedFinancialData?.finance_notes || '',
          billingAddress: typedFinancialData?.billing_address || '',
          billingCity: typedFinancialData?.billing_city || '',
          billingState: typedFinancialData?.billing_state || '',
          billingZip: typedFinancialData?.billing_zip || ''
        });
      } catch (error) {
        console.error("Error loading settings:", error);
        toast.error("Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  // Save settings to respective database tables
  const saveSettings = async (newSettings: AppSettings) => {
    try {
      // Update defaults in the management_companies table
      const { error: mgmtError } = await supabase
        .from('management_companies')
        .update({
          default_pricing_table_id: newSettings.defaultPricingTableId ? parseInt(newSettings.defaultPricingTableId) : null,
          default_document_groups: newSettings.defaultDocumentGroups || []
        })
        .eq('id', 1); // Using record with ID 1 for platform defaults
      
      if (mgmtError) throw mgmtError;
      
      // Update financial settings directly in the platform_settings table
      const { error: financeError } = await supabase
        .from('platform_settings')
        .update({
          account_holder_name: newSettings.accountHolderName || '',
          bank_name: newSettings.bankName || '',
          account_number: newSettings.accountNumber || '',
          routing_number: newSettings.routingNumber || '',
          account_type: newSettings.accountType || '',
          wire_instructions: newSettings.wireInstructions || '',
          finance_notes: newSettings.financeNotes || '',
          billing_address: newSettings.billingAddress || '',
          billing_city: newSettings.billingCity || '',
          billing_state: newSettings.billingState || '',
          billing_zip: newSettings.billingZip || ''
        })
        .eq('id', 1);
      
      if (financeError) throw financeError;
      
      setSettings(newSettings);
      toast.success("Settings saved successfully");
      return true;
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
      return false;
    }
  };

  return {
    settings,
    isLoading,
    saveSettings
  };
};
